import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getUser, getIntercomToken } from "~/api/account/requests";
import { AccountContext } from "~/contexts/Account";
import { IAccount } from "~/types/account";
import { Userpilot } from "userpilot";
import _debounce from "lodash/debounce";
import { useIntercom } from "react-use-intercom";
import { intercomKey } from "~/helpers/environment";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { useStore } from "~/hooks/useStore";
import { log } from "~/helpers/datadog";

export const useAccount = () => {
  const { account, setAccount } = useContext(AccountContext);
  const [isLoading, setIsLoading] = useState(false);
  const { boot } = useIntercom();
  const { store } = useStore();
  const intercomBooted = useRef(false);
  const APP_IDENTIFIER = "dropshiptool-app";

  const bootIntercomForAccount = async (account: IAccount) => {
    if (!intercomKey) {
      return;
    }

    let intercom_user_jwt: string;
    try {
      ({ intercom_user_jwt } = await getIntercomToken());
    } catch (error) {
      log.error("Failed to fetch Intercom token", error);
      return;
    }

    boot({
      email: account.email,
      userId: account.id.toString(),
      name: account.name,
      intercomUserJwt: intercom_user_jwt,
      customAttributes: {
        app: APP_IDENTIFIER,
        shop_name: store?.name,
        shop_url: store?.url,
        shop_email: store?.email,
        shop_phone: store?.phone,
      },
    } as Parameters<typeof boot>[0]);
  };

  const bootIntercom = () => {
    if (!account || intercomBooted.current) return;
    intercomBooted.current = true;
    bootIntercomForAccount(account);
  };

  const setAccountWithThirdParties = (acc: IAccount | null) => {
    setAccount(acc);
    if (acc) {
      Userpilot.identify(acc.id.toString(), {
        email: acc.email,
        created_at: acc.created_at,
        name: acc.name,
        payment_provider: acc.payment_provider,
        signup_origin: acc.signup_origin,
      });
      if (
        (acc.active_subscription || DISABLE_PAYMENTS) &&
        !intercomBooted.current
      ) {
        intercomBooted.current = true;
        bootIntercomForAccount(acc);
      }
    }
  };

  const getAccount = async () => {
    setIsLoading(true);
    const profile = (await getUser()) as IAccount;
    setAccountWithThirdParties(profile);
    setIsLoading(false);
    return profile;
  };
  const debouncedGetAccount = useCallback(_debounce(getAccount, 200), []);

  useEffect(() => {
    if (!account) {
      setIsLoading(true);
      debouncedGetAccount();
    }
  }, []);

  return {
    account,
    setAccount: setAccountWithThirdParties,
    getAccount,
    refetch: getAccount,
    bootIntercom,
    isLoading,
  };
};
